import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  type DownloadMeterDataCsvBetweenSpecificDatesPayload,
  type DownloadMeterDataCsvFromStartDatePayload,
  type UpdateCompanyAlarmPayload
} from './companies-types'
import {
  DownloadMeterDataCsvBetweenSpecificDatesAction,
  DownloadMeterDataCsvFromStartDateAction,
  UpdateCompanyAlarmAction
} from './companies-actions'
import selectIdToken from '../selectors/select-id-token'
import fileDownload from 'js-file-download'

import { LogoutSuccessAction } from '../auth/auth-actions'
import { AddMessageAction } from '../messages/messages-actions'
import { MessageTypes } from '../messages/messages-types'
import { SetCompanyAlarmAction } from '../meter-table/meter-table-actions'
import { selectFromState } from '../selectors/select-from-state'
import { ApiUtil } from '../../api/api-utils'
import { CompanyApi, type SetCompanyAlarmResponse } from '../../api/company-api'
import { type DownloadFileResponse } from '../../api/types'
import { generateExportCsvFileName } from '../../utils/utils'

/* Sage Generator Functions */

// Note: disabling requiring explicit return types for saga generators
// (as the type is pretty complex and produces little value as explicit)
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * downloadMeterDataCsvStartingFromDate(action: { payload: DownloadMeterDataCsvFromStartDatePayload }) {
  const input = {
    downloadType: action.payload.downloadType,
    startDate: action.payload.downloadDateSelected
  }

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: DownloadFileResponse = yield call(CompanyApi.downloadMeterDataCsvStartingFromDate, idToken, input)

  if (response.succeeded) {
    const date = new Date(action.payload.downloadDateSelected)
    const filename = `export${date.getFullYear()}${date.getMonth() + 1}${date.getDate()}.csv`
    fileDownload(response.payload.content, filename)
  } else {
    yield put(AddMessageAction(response.errorKey ?? 'companies.fileDownloadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * downloadMeterDataCsvWithinSpecificDates(action: {
  payload: DownloadMeterDataCsvBetweenSpecificDatesPayload
}) {
  const input = {
    startDate: action.payload.startDateSelected,
    endDate: action.payload.endDateSelected
  }
  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)
  const response: DownloadFileResponse = yield call(CompanyApi.downloadMeterDataCsvWithinSpecificDates, idToken, input)

  if (response.succeeded) {
    const startDate = new Date(action.payload.startDateSelected)
    const endDate = new Date(action.payload.endDateSelected)
    fileDownload(response.payload.content, generateExportCsvFileName(startDate, endDate))
  } else {
    yield put(AddMessageAction(response.errorKey ?? 'companies.fileDownloadFailedDefaultError', MessageTypes.error))
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function * setCompanyAlarm(action: { payload: UpdateCompanyAlarmPayload }) {
  const { companyAlarm } = action.payload

  // note: 'select' function do not work well with TypeScript types
  // (i.e. the return value do not reflect the correct type)
  const idToken: ReturnType<typeof selectIdToken> = yield selectFromState(selectIdToken)

  const response: SetCompanyAlarmResponse = yield call(CompanyApi.setCompanyAlarm, idToken, companyAlarm)
  if (response.succeeded) {
    yield put(AddMessageAction('company_alarm_dialog.update_success_toast', MessageTypes.success))
    yield put(SetCompanyAlarmAction({
      companyId: null,
      alarmType: null,
      state: false,
      maxFlowPerHour: null
    }))
  } else {
    yield put(AddMessageAction('company_alarm_dialog.update_failed_toast', MessageTypes.error))
    if (ApiUtil.shouldForceLogout(response)) {
      yield put(LogoutSuccessAction())
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function * companiesSagas() {
  yield takeEvery(DownloadMeterDataCsvFromStartDateAction, downloadMeterDataCsvStartingFromDate)
  yield takeEvery(DownloadMeterDataCsvBetweenSpecificDatesAction, downloadMeterDataCsvWithinSpecificDates)
  yield takeLatest(UpdateCompanyAlarmAction, setCompanyAlarm)
}
